import React from 'react';

import { BrowserRouter as Router, Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';

import { Breadcrumb } from '@buildoutinc/blueprint-react/ui/Breadcrumb';
import { Tab, Tabs, TabsList } from '@buildoutinc/blueprint-react/ui/Tabs';

import CollectionPages from './collection_pages';
import SlotSettings from './slot_settings';
import { MarketOverviewsTab } from './types';

interface Props {
  basePath: string;
  libraryUrl: string;
  companyId: string | null;
}

const TAB_LABELS: Record<MarketOverviewsTab, string> = {
  [MarketOverviewsTab.Pages]: 'Pages',
  [MarketOverviewsTab.SlotSettings]: 'Slot settings',
};

const tabFromPath = (pathname: string): MarketOverviewsTab => {
  if (pathname.startsWith(`/${MarketOverviewsTab.SlotSettings}`)) return MarketOverviewsTab.SlotSettings;

  return MarketOverviewsTab.Pages;
};

function MarketOverviews({ basePath, libraryUrl, companyId }: Props) {
  const history = useHistory();
  const location = useLocation();

  // The search carries company_id for a parent company user, which the server needs on a reload.
  const openTab = (tab: string) => history.push({ pathname: `/${tab}`, search: location.search });

  return (
    <div className="container my-4 pb-7">
      <Breadcrumb className="mb-3">
        <Breadcrumb.List>
          <Breadcrumb.Item>
            <Breadcrumb.Link href={libraryUrl}>Page Library</Breadcrumb.Link>
          </Breadcrumb.Item>
          <Breadcrumb.Separator />
          <Breadcrumb.Item>
            <Breadcrumb.Page>Market Overviews</Breadcrumb.Page>
          </Breadcrumb.Item>
        </Breadcrumb.List>
      </Breadcrumb>

      <h4 className="mb-1">Market Overviews</h4>
      <p className="text-muted mb-3">
        Market pages that fill the Market Overview slot. Each document with the slot gets the pages that match its
        property&apos;s market.
      </p>

      <Tabs className="mb-4" orientation="horizontal" value={tabFromPath(location.pathname)} onValueChange={openTab}>
        <TabsList>
          {Object.values(MarketOverviewsTab).map(tab => (
            <Tab key={tab} value={tab}>{TAB_LABELS[tab]}</Tab>
          ))}
        </TabsList>
      </Tabs>

      <Switch>
        <Route path={`/${MarketOverviewsTab.Pages}`}>
          <CollectionPages companyId={companyId} libraryBase={basePath.replace(/\/market_overviews$/, '')} />
        </Route>
        <Route path={`/${MarketOverviewsTab.SlotSettings}`}>
          <SlotSettings companyId={companyId} slotDocumentsUrl={`${basePath}/slot_documents`} />
        </Route>
        <Redirect to={{ pathname: `/${MarketOverviewsTab.Pages}`, search: location.search }} />
      </Switch>
    </div>
  );
}

export default function MarketOverviewsPage(props: Props) {
  return (
    <Router basename={props.basePath}>
      <MarketOverviews {...props} />
    </Router>
  );
}
